import { Link } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';
import { Shield, ArrowLeft, Lock, Database, Trash2 } from 'lucide-react';

export default function Privacy() {
  const { language } = useTranslation();

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#050505]/90 backdrop-blur-lg sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
            <span className="font-heading text-lg tracking-tight">SheetArmor</span>
          </div>
          <Link to="/" className="flex items-center gap-2 text-neutral-400 hover:text-white text-sm font-body transition-colors">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            <span>Back</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-heading text-4xl text-white mb-2">
          {language === 'fr' ? 'Politique de Confidentialité' : 'Privacy Policy'}
        </h1>
        <p className="text-neutral-500 text-sm font-body mb-10">
          {language === 'fr' ? 'Dernière mise à jour : 25 février 2026' : 'Last updated: February 25, 2026'}
        </p>

        {/* Zero-Retention Badge */}
        <div className="flex items-center gap-3 p-4 bg-blue-600/10 border border-blue-600/30 rounded-sm mb-10">
          <Lock className="w-6 h-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
          <div>
            <h3 className="text-white font-heading text-sm mb-1">
              {language === 'fr' ? 'Architecture Zero-Retention' : 'Zero-Retention Architecture'}
            </h3>
            <p className="text-blue-400 text-xs font-body">
              {language === 'fr'
                ? 'Vos fichiers Excel sont supprimés immédiatement après génération du rapport PDF. Aucune donnée n\'est conservée ni utilisée pour entraîner des modèles IA.'
                : 'Your Excel files are deleted immediately after PDF report generation. No data is retained or used to train AI models.'}
            </p>
          </div>
        </div>

        {language === 'fr' ? (
          <div className="space-y-8 text-neutral-300 font-body leading-relaxed">
            <section>
              <h2 className="text-xl text-white font-heading mb-3">1. Introduction</h2>
              <p>
                SheetArmor ("nous", "notre") s'engage à protéger la confidentialité de vos données. Cette politique décrit comment nous collectons,
                utilisons et protégeons les informations personnelles et professionnelles dans le cadre de notre service d'audit de fichiers Excel.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3 flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                2. Données Collectées
              </h2>

              <h3 className="text-lg text-white font-heading mb-2 mt-4">2.1 Données de Compte</h3>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                <li>Nom complet</li>
                <li>Adresse email professionnelle</li>
                <li>Mot de passe (chiffré avec bcrypt)</li>
                <li>Nom de domaine de l'organisation</li>
                <li>Plan d'abonnement (Free, Auditor, Firm)</li>
              </ul>

              <h3 className="text-lg text-white font-heading mb-2 mt-4">2.2 Données d'Utilisation</h3>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                <li>Historique des audits (nom de fichier, date, score de santé)</li>
                <li>Adresse IP (uniquement pour le rate-limiting)</li>
                <li>Métadonnées techniques (navigateur, système d'exploitation)</li>
              </ul>

              <h3 className="text-lg text-white font-heading mb-2 mt-4">2.3 Fichiers Excel</h3>
              <p className="mb-2">
                <strong className="text-white">CRITIQUE — Architecture Zero-Retention :</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 bg-neutral-900/50 p-4 rounded-sm border border-white/5">
                <li>
                  <strong>Durée de stockage :</strong> Les fichiers Excel (.xlsx) sont stockés temporairement en mémoire serveur <span className="text-blue-400">uniquement pendant l'analyse</span>
                  (durée moyenne : 30-90 secondes)
                </li>
                <li>
                  <strong>Suppression automatique :</strong> Immédiatement après la génération du rapport PDF, le fichier Excel source est <span className="text-red-400">définitivement supprimé</span>
                  de nos serveurs
                </li>
                <li>
                  <strong>Rapports PDF :</strong> Les rapports PDF générés sont conservés pendant <span className="text-blue-400">24 heures</span> uniquement,
                  puis automatiquement purgés de la base de données
                </li>
                <li>
                  <strong>Zéro persistance :</strong> Aucune copie, backup ou archive des fichiers sources n'est créée. Nous ne conservons aucune cellule, formule ou donnée brute
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3 flex items-center gap-2">
                <Lock className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                3. Traitement par l'IA Tierce (Anthropic Claude)
              </h2>
              <p className="mb-3">
                SheetArmor utilise l'API Anthropic Claude (modèle Sonnet 4) pour générer les rapports d'analyse en langage naturel.
                <strong className="text-white"> Garanties contractuelles Anthropic :</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 bg-neutral-900/50 p-4 rounded-sm border border-white/5">
                <li>
                  <strong>Zéro entraînement :</strong> Conformément aux <a href="https://www.anthropic.com/legal/commercial-terms" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400 underline">Conditions B2B d'Anthropic</a>,
                  les données soumises via l'API ne sont <span className="text-blue-400">jamais utilisées pour entraîner, améliorer ou affiner leurs modèles IA</span>
                </li>
                <li>
                  <strong>Suppression immédiate :</strong> Anthropic s'engage à ne pas conserver les prompts ni les réponses au-delà du temps nécessaire au traitement de la requête API
                </li>
                <li>
                  <strong>Conformité RGPD :</strong> Anthropic est conforme au RGPD (clauses contractuelles types UE)
                </li>
                <li>
                  <strong>Pas de revue humaine :</strong> Aucun employé d'Anthropic n'accède à vos données dans le cadre de l'API commerciale
                </li>
              </ul>
              <p className="mt-3 text-sm text-neutral-400">
                Pour plus d'informations : <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400 underline">Politique de Confidentialité Anthropic</a>
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">4. Base Juridique du Traitement (RGPD)</h2>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Exécution du contrat :</strong> Traitement nécessaire pour fournir le service d'audit</li>
                <li><strong>Consentement :</strong> Vous consentez explicitement au traitement lors de l'inscription</li>
                <li><strong>Intérêt légitime :</strong> Prévention de la fraude, amélioration du service, support technique</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">5. Partage des Données</h2>
              <p className="mb-2">Nous ne vendons jamais vos données. Nous les partageons uniquement avec :</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Anthropic (Claude API) :</strong> Traitement IA des rapports (cf. section 3)</li>
                <li><strong>Stripe :</strong> Traitement des paiements (nom, email, montant)</li>
                <li><strong>MongoDB Atlas :</strong> Hébergement base de données (chiffrement au repos)</li>
                <li><strong>Autorités judiciaires :</strong> Uniquement si requis par la loi (assignation, mandat)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">6. Sécurité</h2>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Chiffrement TLS/HTTPS pour toutes les communications</li>
                <li>Mots de passe chiffrés avec bcrypt (salt + hash)</li>
                <li>Sessions sécurisées avec cookies HTTP-only</li>
                <li>Authentification B2B stricte (emails professionnels uniquement)</li>
                <li>Rate-limiting pour prévenir les abus</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3 flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-500" strokeWidth={1.5} />
                7. Vos Droits (RGPD)
              </h2>
              <p className="mb-2">Conformément au RGPD, vous disposez des droits suivants :</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Droit d'accès :</strong> Obtenir une copie de vos données personnelles</li>
                <li><strong>Droit de rectification :</strong> Corriger les données inexactes</li>
                <li><strong>Droit à l'effacement :</strong> Supprimer votre compte et toutes vos données (via Paramètres)</li>
                <li><strong>Droit d'opposition :</strong> Refuser le traitement à des fins de marketing (nous n'en faisons pas)</li>
                <li><strong>Droit à la portabilité :</strong> Exporter vos données dans un format structuré (JSON)</li>
              </ul>
              <p className="mt-3">
                Pour exercer ces droits : <a href="mailto:hello@sheetarmor.io" className="text-blue-500 hover:text-blue-400">hello@sheetarmor.io</a>
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">8. Cookies</h2>
              <p>
                Nous utilisons uniquement un cookie de session (<code className="text-blue-400 bg-neutral-900 px-1">session_token</code>)
                pour maintenir votre authentification. Aucun cookie de tracking ou publicitaire.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">9. Transferts Internationaux</h2>
              <p>
                Vos données peuvent être traitées dans l'Union Européenne et aux États-Unis (serveurs Anthropic).
                Tous les transferts hors UE sont sécurisés par des clauses contractuelles types approuvées par la Commission Européenne.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">10. Modifications</h2>
              <p>
                Nous nous réservons le droit de modifier cette politique. Les utilisateurs seront informés par email des changements importants.
                La date de dernière mise à jour est indiquée en haut de cette page.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">11. Contact & DPO</h2>
              <p>
                Pour toute question relative à la protection de vos données personnelles, contactez notre Délégué à la Protection des Données (DPO) :
                <a href="mailto:hello@sheetarmor.io" className="text-blue-500 hover:text-blue-400 ml-1">hello@sheetarmor.io</a>
              </p>
              <p className="mt-2">
                Vous pouvez également déposer une réclamation auprès de la CNIL (France) : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400">www.cnil.fr</a>
              </p>
            </section>
          </div>
        ) : (
          <div className="space-y-8 text-neutral-300 font-body leading-relaxed">
            <section>
              <h2 className="text-xl text-white font-heading mb-3">1. Introduction</h2>
              <p>
                SheetArmor ("we", "our") is committed to protecting the privacy of your data. This policy describes how we collect,
                use, and protect personal and professional information as part of our Excel file auditing service.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3 flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                2. Data Collected
              </h2>

              <h3 className="text-lg text-white font-heading mb-2 mt-4">2.1 Account Data</h3>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                <li>Full name</li>
                <li>Corporate email address</li>
                <li>Password (encrypted with bcrypt)</li>
                <li>Organization domain name</li>
                <li>Subscription plan (Free, Auditor, Firm)</li>
              </ul>

              <h3 className="text-lg text-white font-heading mb-2 mt-4">2.2 Usage Data</h3>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                <li>Audit history (filename, date, health score)</li>
                <li>IP address (for rate-limiting only)</li>
                <li>Technical metadata (browser, operating system)</li>
              </ul>

              <h3 className="text-lg text-white font-heading mb-2 mt-4">2.3 Excel Files</h3>
              <p className="mb-2">
                <strong className="text-white">CRITICAL — Zero-Retention Architecture:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 bg-neutral-900/50 p-4 rounded-sm border border-white/5">
                <li>
                  <strong>Storage duration:</strong> Excel files (.xlsx) are temporarily stored in server memory <span className="text-blue-400">only during analysis</span>
                  (average duration: 30-90 seconds)
                </li>
                <li>
                  <strong>Automatic deletion:</strong> Immediately after PDF report generation, the source Excel file is <span className="text-red-400">permanently deleted</span>
                  from our servers
                </li>
                <li>
                  <strong>PDF reports:</strong> Generated PDF reports are kept for <span className="text-blue-400">24 hours</span> only,
                  then automatically purged from the database
                </li>
                <li>
                  <strong>Zero persistence:</strong> No copies, backups, or archives of source files are created. We retain no cells, formulas, or raw data
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3 flex items-center gap-2">
                <Lock className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                3. Third-Party AI Processing (Anthropic Claude)
              </h2>
              <p className="mb-3">
                SheetArmor uses the Anthropic Claude API (Sonnet 4 model) to generate natural language analysis reports.
                <strong className="text-white"> Anthropic contractual guarantees:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 bg-neutral-900/50 p-4 rounded-sm border border-white/5">
                <li>
                  <strong>Zero training:</strong> According to <a href="https://www.anthropic.com/legal/commercial-terms" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400 underline">Anthropic's B2B Terms</a>,
                  data submitted via the API is <span className="text-blue-400">never used to train, improve, or fine-tune their AI models</span>
                </li>
                <li>
                  <strong>Immediate deletion:</strong> Anthropic commits to not retaining prompts or responses beyond the time necessary for API request processing
                </li>
                <li>
                  <strong>GDPR compliance:</strong> Anthropic is GDPR compliant (EU Standard Contractual Clauses)
                </li>
                <li>
                  <strong>No human review:</strong> No Anthropic employees access your data as part of the commercial API
                </li>
              </ul>
              <p className="mt-3 text-sm text-neutral-400">
                More information: <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400 underline">Anthropic Privacy Policy</a>
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">4. Legal Basis for Processing (GDPR)</h2>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Contract execution:</strong> Processing necessary to provide the auditing service</li>
                <li><strong>Consent:</strong> You explicitly consent to processing upon registration</li>
                <li><strong>Legitimate interest:</strong> Fraud prevention, service improvement, technical support</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">5. Data Sharing</h2>
              <p className="mb-2">We never sell your data. We only share it with:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Anthropic (Claude API):</strong> AI processing of reports (see section 3)</li>
                <li><strong>Stripe:</strong> Payment processing (name, email, amount)</li>
                <li><strong>MongoDB Atlas:</strong> Database hosting (encryption at rest)</li>
                <li><strong>Judicial authorities:</strong> Only if required by law (subpoena, warrant)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">6. Security</h2>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>TLS/HTTPS encryption for all communications</li>
                <li>Passwords encrypted with bcrypt (salt + hash)</li>
                <li>Secure sessions with HTTP-only cookies</li>
                <li>Strict B2B authentication (corporate emails only)</li>
                <li>Rate-limiting to prevent abuse</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3 flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-500" strokeWidth={1.5} />
                7. Your Rights (GDPR)
              </h2>
              <p className="mb-2">Under GDPR, you have the following rights:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Right of access:</strong> Obtain a copy of your personal data</li>
                <li><strong>Right to rectification:</strong> Correct inaccurate data</li>
                <li><strong>Right to erasure:</strong> Delete your account and all your data (via Settings)</li>
                <li><strong>Right to object:</strong> Refuse processing for marketing purposes (we don't do marketing)</li>
                <li><strong>Right to data portability:</strong> Export your data in structured format (JSON)</li>
              </ul>
              <p className="mt-3">
                To exercise these rights: <a href="mailto:hello@sheetarmor.io" className="text-blue-500 hover:text-blue-400">hello@sheetarmor.io</a>
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">8. Cookies</h2>
              <p>
                We only use a session cookie (<code className="text-blue-400 bg-neutral-900 px-1">session_token</code>)
                to maintain your authentication. No tracking or advertising cookies.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">9. International Transfers</h2>
              <p>
                Your data may be processed in the European Union and the United States (Anthropic servers).
                All transfers outside the EU are secured by Standard Contractual Clauses approved by the European Commission.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">10. Modifications</h2>
              <p>
                We reserve the right to modify this policy. Users will be notified by email of significant changes.
                The last update date is shown at the top of this page.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">11. Contact & DPO</h2>
              <p>
                For any questions regarding the protection of your personal data, contact our Data Protection Officer (DPO):
                <a href="mailto:hello@sheetarmor.io" className="text-blue-500 hover:text-blue-400 ml-1">hello@sheetarmor.io</a>
              </p>
              <p className="mt-2">
                You can also file a complaint with CNIL (France): <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400">www.cnil.fr</a>
              </p>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
